import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bookmark, Briefcase, MapPin, Building2, DollarSign, ExternalLink, Trash2
} from "lucide-react";
import { toast } from "sonner";

const salaryDisplay = (j) => {
  if (j.salary_text) return j.salary_text;
  if (j.salary_min && j.salary_max) {
    return j.salary_min >= 1000
      ? `$${(j.salary_min/1000).toFixed(0)}k - $${(j.salary_max/1000).toFixed(0)}k`
      : `$${j.salary_min}-$${j.salary_max}/hr`;
  }
  return null;
};

export default function SavedJobs() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/jobs/saved/list`, { headers: { Authorization: `Bearer ${token}` } });
      setJobs(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/jobs/saved/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Removed"));
      setJobs((p) => p.filter((x) => x.job_id !== id));
    } catch (e) { toast.error(t("فشل الحذف", "Remove failed")); }
  };

  return (
    <DashboardLayout title={t("وظائفي المحفوظة", "Saved Jobs")}>
      <div className="space-y-6" data-testid="saved-jobs-page">
        <div className="relative glass-card rounded-3xl p-6 lg:p-8 bg-luxury overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-yellow-500/20 blur-[100px] rounded-full" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-purple-500/20 blur-[100px] rounded-full" />
          <div className="relative flex items-center justify-between gap-4 flex-wrap">
            <div>
              <Badge className="bg-yellow-500/20 text-yellow-400 mb-2"><Bookmark size={12} className="ml-1" />{t("وظائفك", "Your jobs")}</Badge>
              <h2 className="text-2xl lg:text-3xl font-bold text-luxury">{t("الوظائف التي حفظتها", "Jobs you've saved")}</h2>
              <p className="text-slate-400 text-sm mt-1">{t("راجع وتقدّم في أي وقت", "Review and apply anytime")}</p>
            </div>
            <Link to="/jobs">
              <Button variant="outline" className="border-slate-700 text-white">{t("ابحث عن المزيد", "Find more jobs")}</Button>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1,2,3].map((i) => <div key={i} className="glass-card rounded-2xl h-56 skeleton" />)}
          </div>
        ) : jobs.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <Briefcase className="text-slate-600 mx-auto mb-3" size={40} />
            <p className="text-slate-400 mb-4">{t("لم تحفظ أي وظائف بعد", "No saved jobs yet")}</p>
            <Link to="/jobs"><Button className="btn-primary">{t("ابدأ البحث", "Start searching")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((j) => {
              const salary = salaryDisplay(j);
              return (
                <div key={j.job_id} className="glass-card rounded-2xl p-5 purple-gold-glow" data-testid={`saved-job-${j.job_id}`}>
                  <h3 className="font-bold text-lg text-white truncate mb-1">{j.title}</h3>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300 mb-2">
                    <Building2 size={14} className="text-yellow-500" /> {j.company}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mb-3 flex-wrap">
                    <span className="flex items-center gap-1"><MapPin size={12} className="text-yellow-500" />{j.city}, {j.state}</span>
                    {salary && <span className="flex items-center gap-1 text-yellow-400"><DollarSign size={12} />{salary}</span>}
                  </div>
                  <p className="text-sm text-slate-400 line-clamp-2 mb-4">{j.description}</p>
                  <div className="flex gap-2">
                    {j.apply_url && (
                      <a href={j.apply_url} target="_blank" rel="noreferrer" className="flex-1">
                        <Button className="w-full btn-primary">{t("تقدّم", "Apply")} <ExternalLink size={14} className="ml-2" /></Button>
                      </a>
                    )}
                    <Button onClick={() => remove(j.job_id)} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid={`remove-saved-${j.job_id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
